import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import type { Annotation as AnnotationType } from '../types';

const DEFAULT_COLOR = '#FFEB3B';

// 生成唯一ID
const generateId = () => {
  return `annotation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export interface AnnotationProps {
  pageNumber: number;
  scale: number;
  isAnnotating: boolean;
  annotations: AnnotationType[];
  onAnnotationAdd?: (annotation: AnnotationType) => void;
  onAnnotationDelete?: (annotationId: string) => void;
  readOnly?: boolean;
}

export const Annotation: React.FC<AnnotationProps> = ({
  pageNumber,
  scale,
  isAnnotating,
  annotations = [],
  onAnnotationAdd,
  onAnnotationDelete,
  readOnly = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState<string | null>(null);

    // 移除复杂的容器尺寸监听，使用 inset-0 自动覆盖

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isAnnotating || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      // 转换为百分比坐标，考虑缩放因素
      // rect.width 和 rect.height 是缩放后的尺寸，需要除以 scale 得到未缩放尺寸
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      console.log('鼠标按下位置:', { clientX: e.clientX, clientY: e.clientY, rect, x, y, scale });
      setIsDrawing(true);
      setStartPoint({ x, y });
    },
    [isAnnotating, scale]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDrawing || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      // 转换为百分比坐标，考虑缩放因素
      // rect.width 和 rect.height 是缩放后的尺寸，直接使用即可
      const currentX = (e.clientX - rect.left) / rect.width;
      const currentY = (e.clientY - rect.top) / rect.height;

      setCurrentRect({
        x: Math.min(startPoint.x, currentX),
        y: Math.min(startPoint.y, currentY),
        width: Math.abs(currentX - startPoint.x),
        height: Math.abs(currentY - startPoint.y),
      });
    },
    [isDrawing, startPoint, scale]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !currentRect) return;

    setIsDrawing(false);
    if (currentRect.width < 5 || currentRect.height < 5) {
      setCurrentRect(null);
      return;
    }

    const newAnnotation: AnnotationType = {
      id: generateId(),
      pageNumber,
      ...currentRect,
      content: '',
      color: DEFAULT_COLOR,
    };

    onAnnotationAdd?.(newAnnotation);
    setCurrentRect(null);
  }, [isDrawing, currentRect, pageNumber, onAnnotationAdd]);

  const handleAnnotationClick = useCallback((annotationId: string) => {
    setSelectedAnnotation(selectedAnnotation === annotationId ? null : annotationId);
  }, [selectedAnnotation]);

  const handleContentChange = useCallback((annotation: AnnotationType, content: string) => {
    const updatedAnnotation = {
      ...annotation,
      content,
    };
    onAnnotationAdd?.(updatedAnnotation);
  }, [onAnnotationAdd]);

  const handleDeleteAnnotation = useCallback((annotationId: string) => {
    onAnnotationDelete?.(annotationId);
    setSelectedAnnotation(null);
  }, [onAnnotationDelete]);

  console.log('标注组件渲染:', {
    pageNumber,
    annotations: annotations.length,
    currentPageAnnotationsCount: annotations.filter(a => a.pageNumber === pageNumber).length,
    currentPageAnnotations: annotations.filter(a => a.pageNumber === pageNumber)
  });

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 10,
        pointerEvents: 'auto', // 确保可以接收鼠标事件
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* 当前绘制的标注 */}
      {currentRect && (
        <div
          className="absolute border-2 border-yellow-400 bg-yellow-100/30"
          style={{
            left: `${currentRect.x * 100}%`,
            top: `${currentRect.y * 100}%`,
            width: `${currentRect.width * 100}%`,
            height: `${currentRect.height * 100}%`,
          }}
        />
      )}

      {/* 已保存的标注 */}
      {annotations
        .filter(anno => anno.pageNumber === pageNumber)
        .map(annotation => {
          console.log('渲染标注:', annotation.id, `位置: ${annotation.x * 100}%, ${annotation.y * 100}%`);
          return (
            <div key={annotation.id} style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,

            }} >
                              <div
                  className='border border-solid'
                  style={{
                    position: 'absolute',
                    left: `${annotation.x * 100}%`,
                    top: `${annotation.y * 100}%`,
                    width: `${annotation.width * 100}%`,
                    height: `${annotation.height * 100}%`,
                    borderWidth: '2px',
                    borderColor: `${annotation.color}`,
                    backgroundColor: `${annotation.color}4D`, // 30%透明度 (4D = 77/255 ≈ 0.3)
                    zIndex: 1000,
                  }}
                onClick={() => handleAnnotationClick(annotation.id)}
              />
            </div>
          );
        })}
    </div>
  );
};

export default Annotation;
